// Orchestrator V2 — subagent run aggregation.
//
// The backend streams a dispatch_subagent invocation as a sequence of side-
// channel UIMessage data parts (data-subagent-start/event/complete/hitl), each
// carrying the dispatch's callId as `runId`. This module folds those parts back
// into per-run SubagentRun objects so both the right-rail subagent cards
// (SubagentRail) and the chat-level interrupt (MessageList) can read the same
// derived state.

import { useMemo } from "react";
import type { UIMessage } from "ai";

export interface SubagentEvent {
  event: "text-delta" | "tool-call" | "tool-result" | "error";
  text?: string;
  toolName?: string;
  input?: unknown;
  output?: unknown;
  error?: string;
}

export interface SubagentPendingHitl {
  toolName: string;
  // The awaiting-user-input envelope returned by the HITL tool — shape depends
  // on toolName. It matches the output shape the supervisor HITL tools produce,
  // so the same panel renderer handles both.
  envelope: Record<string, unknown>;
}

export interface SubagentRun {
  id: string;
  kind: "connector" | "planner" | "explore" | "general";
  // Short name the supervisor gave the dispatch (the `name` arg), used as the
  // card's purpose line. Falls back to the kind on the backend when unnamed.
  label?: string;
  task: string;
  status: "running" | "complete" | "error" | "cancelled";
  events: SubagentEvent[];
  finalText?: string;
  error?: string;
  durationMs?: number;
  modelId?: string;
  // Set when the sub-agent halted on a HITL tool call mid-run. Cleared once the
  // run resumes (data-subagent-complete arrives).
  pendingHitl?: SubagentPendingHitl;
}

export interface SubagentInterrupt {
  runId: string;
  toolName: string;
  envelope: Record<string, unknown>;
}

// Memoize aggregation by a cheap key so we don't re-walk the entire message
// history on every token tick during a long stream — only the trailing message
// grows mid-stream.
export function useSubagentRuns(messages: UIMessage[]): Map<string, SubagentRun> {
  const last = messages[messages.length - 1];
  const key = `${messages.length}:${last?.parts?.length ?? 0}:${last?.id ?? ""}`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => aggregateSubagents(messages), [key]);
}

// Rail ordering: still-running (and paused-for-input) runs first so active work
// is visible without scrolling, then finished runs most-recent-first.
export function orderSubagentRuns(map: Map<string, SubagentRun>): SubagentRun[] {
  const all = [...map.values()];
  const active = all.filter((r) => r.status === "running");
  const finished = all.filter((r) => r.status !== "running").reverse();
  return [...active, ...finished];
}

// The subagent currently paused on a HITL tool, if any. Last one wins — only
// one subagent pauses the supervisor loop at a time, but iterate defensively.
export function findPendingSubagentInterrupt(map: Map<string, SubagentRun>): SubagentInterrupt | null {
  let found: SubagentInterrupt | null = null;
  for (const r of map.values()) {
    if (r.pendingHitl) {
      found = { runId: r.id, toolName: r.pendingHitl.toolName, envelope: r.pendingHitl.envelope };
    }
  }
  return found;
}

function pickString(o: Record<string, unknown>, key: string): string | undefined {
  const v = o[key];
  return typeof v === "string" ? v : undefined;
}

export function aggregateSubagents(messages: UIMessage[]): Map<string, SubagentRun> {
  const map = new Map<string, SubagentRun>();
  const ensure = (id: string): SubagentRun => {
    let r = map.get(id);
    if (!r) {
      r = { id, kind: "general", task: "", status: "running", events: [] };
      map.set(id, r);
    }
    return r;
  };

  for (const m of messages) {
    for (const p of m.parts) {
      const rec = p as Record<string, unknown>;
      if (p.type === "data-subagent-start") {
        const id = pickString(rec, "id");
        const data = rec["data"] as { kind: SubagentRun["kind"]; label?: string; task: string } | undefined;
        if (id && data) {
          const existing = ensure(id);
          existing.kind = data.kind;
          existing.label = data.label;
          existing.task = data.task;
        }
      } else if (p.type === "data-subagent-event") {
        // The backend writes each event as its own data part with a unique
        // part-level id (so the AI SDK doesn't reconcile/collapse them) and
        // embeds the dispatch's callId as `runId` inside data — group by that.
        const data = rec["data"] as (SubagentEvent & { runId?: string }) | undefined;
        const runId = data?.runId ?? pickString(rec, "id");
        if (runId && data) {
          ensure(runId).events.push(data);
        }
      } else if (p.type === "data-subagent-complete") {
        const id = pickString(rec, "id");
        const data = rec["data"] as
          | {
              status: "complete" | "error" | "cancelled";
              text?: string;
              error?: string;
              durationMs?: number;
              modelId?: string;
            }
          | undefined;
        if (id && data) {
          const existing = ensure(id);
          existing.status = data.status;
          existing.finalText = data.text;
          existing.error = data.error;
          existing.durationMs = data.durationMs;
          existing.modelId = data.modelId;
          // Resume cleared the HITL pause — drop any prior pendingHitl so the
          // panel collapses back to the normal completed view.
          existing.pendingHitl = undefined;
        }
      } else if (p.type === "data-subagent-hitl") {
        const data = rec["data"] as
          | { runId?: string; kind?: SubagentRun["kind"]; toolName?: string; envelope?: Record<string, unknown> }
          | undefined;
        const runId = data?.runId;
        if (runId && data?.toolName && data.envelope) {
          const existing = ensure(runId);
          if (data.kind) existing.kind = data.kind;
          existing.pendingHitl = { toolName: data.toolName, envelope: data.envelope };
        }
      }
    }
  }
  return map;
}
